import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const logo = "/airplx-logo-transparent-square-no-text.png";

type NotFoundType = "hangar" | "ramp";

type Props = {
  type: NotFoundType;
};

export const NotFoundCard: React.FC<Props> = ({ type }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const title =
    type === "hangar"
      ? t("components.notFoundCard.hangarTitle")
      : t("components.notFoundCard.rampTitle");

  const message =
    type === "hangar"
      ? t("components.notFoundCard.hangarMessage")
      : t("components.notFoundCard.rampMessage");

  const buttonText =
    type === "hangar"
      ? t("components.notFoundCard.goToHangars")
      : t("components.notFoundCard.goToRamps");

  const navigateTo = type === "hangar" ? "/hangars" : "/ramps";

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "50vh",
          justifyContent: "center",
        }}
      >
        {/* Header with Logo */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Box
            component="img"
            src={logo}
            alt="AirPlx Logo"
            sx={{
              width: 48,
              height: 48,
              border: "2px solid",
              borderColor: theme.palette.primary.main,
              borderRadius: "50%",
            }}
          />
          <Typography variant="h4" color="primary" fontWeight="bold">
            AirPlx
          </Typography>
        </Stack>

        {/* Error Card */}
        <Card
          sx={{
            textAlign: "center",
            borderRadius: 3,
            boxShadow: 3,
            width: "100%",
            maxWidth: 450,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Error Icon */}
            <ErrorOutlineIcon
              sx={{
                fontSize: 64,
                color: theme.palette.warning.main,
                mb: 2,
              }}
            />

            {/* Title */}
            <Typography variant="h5" fontWeight="medium" sx={{ mb: 2 }}>
              {title}
            </Typography>

            {/* Message */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              {message}
            </Typography>

            {/* Action Button */}
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(navigateTo)}
              sx={{ fontWeight: "medium", minWidth: 160 }}
            >
              {buttonText}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};
